import { dataLocalDe } from '@/lib/oracao';
import type {
  AppInstalado,
  FiltrosLeads,
  Lead,
  LeadExport,
  OpcoesLeads,
  OracaoAdmin,
  ContaSenha,
  LinkSenha,
  PainelPlataforma,
  PreviaAviso,
  ResumoLeads,
} from './types';

// ---- Rótulos de origem / onde (nomes que o Fernando entende) ----

export const ROTULO_ORIGEM: Record<string, string> = {
  ingresso_inscricao: 'Ingressos e inscrições (Pix pelo app)',
  lancado_a_mao: 'Lançado à mão pela direção',
  fichas_online: 'Fichas compradas pelo site',
  caixa_maquininha: 'Caixa · maquininha (cartão ou Pix)',
  caixa_dinheiro: 'Caixa · dinheiro',
  rifa_pix: 'Ação entre amigos · Pix',
  rifa_na_mao: 'Ação entre amigos · vendido na mão',
};

export const ROTULO_ONDE: Record<string, string> = {
  seliga: 'na conta do Se Liga',
  organizador: 'direto no organizador',
  fora: 'com a equipe',
};

export const ROTULO_FORMA: Record<string, string> = {
  pix: 'Pix',
  transferencia: 'Transferência',
  dinheiro: 'Dinheiro',
  outro: 'Outro',
};

export function rotuloOrigem(o: string): string {
  return ROTULO_ORIGEM[o] ?? o;
}

export function rotuloOnde(o: string): string {
  return ROTULO_ONDE[o] ?? o;
}

export function rotuloForma(f: string | null): string {
  if (!f) return '';
  return ROTULO_FORMA[f] ?? f;
}

// ---- Datas ----
// Cuidado: 'YYYY-MM-DD' (data pura) é lida como UTC; dividimos na mão para não
// voltar um dia no fuso do Brasil.
export function formatarDataBR(iso?: string | null): string {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [a, m, d] = iso.split('-');
    return `${d}/${m}/${a}`;
  }
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  const dia = String(dt.getDate()).padStart(2, '0');
  const mes = String(dt.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}/${dt.getFullYear()}`;
}

// Ex.: "Quinta-feira, 15/10" — lê 'YYYY-MM-DD' como data local.
export function rotuloDiaCurto(dia: string): string {
  const d = dataLocalDe(dia);
  if (!d) return formatarDataBR(dia);
  const sem = new Intl.DateTimeFormat('pt-BR', { weekday: 'long' }).format(d);
  const s = sem.charAt(0).toUpperCase() + sem.slice(1);
  return `${s}, ${formatarDataBR(dia)}`;
}

export function horaMinuto(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Porcentagem no formato brasileiro (ex.: 12,5).
export function formatarPct(v: number | null): string | null {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return null;
  return Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

// ---- Normalizador do que a função devolve ----

function numero(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function normalizarPainel(data: unknown): PainelPlataforma | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Partial<PainelPlataforma>;

  const t = (d.totais ?? {}) as Record<string, unknown>;
  const p = (d.pendencias ?? {}) as Record<string, unknown>;

  const eventos = Array.isArray(d.eventos) ? d.eventos : [];

  return {
    quem: typeof d.quem === 'string' ? d.quem : '',
    gerado_em: typeof d.gerado_em === 'string' ? d.gerado_em : '',
    mes: typeof d.mes === 'string' ? d.mes : '',
    aviso: typeof d.aviso === 'string' ? d.aviso : '',
    totais: {
      eventos: numero(t.eventos),
      entrou_centavos: numero(t.entrou_centavos),
      seliga_centavos: numero(t.seliga_centavos),
      seliga_mes_centavos: numero(t.seliga_mes_centavos),
      mercado_pago_centavos: numero(t.mercado_pago_centavos),
      na_conta_seliga_centavos: numero(t.na_conta_seliga_centavos),
      a_repassar_centavos: numero(t.a_repassar_centavos),
      a_cobrar_centavos: numero(t.a_cobrar_centavos),
    },
    pendencias: {
      pix_rifa_sem_numero: numero(p.pix_rifa_sem_numero),
      maquininhas_sem_evento: numero(p.maquininhas_sem_evento),
      repasse_sem_chave_pix: numero(p.repasse_sem_chave_pix),
    },
    eventos: eventos as PainelPlataforma['eventos'],
  };
}

// ---- Aba "Ajudar com senha" (senha_link_buscar / senha_link_gerar) ----

export function normalizarContasSenha(data: unknown): ContaSenha[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => {
      const c = (item ?? {}) as Record<string, unknown>;
      return {
        usuario_id: stringDe(c.usuario_id),
        nome: typeof c.nome === 'string' ? c.nome : null,
        email: typeof c.email === 'string' ? c.email : null,
        telefone:
          typeof c.telefone === 'string'
            ? c.telefone
            : typeof c.whatsapp === 'string'
              ? c.whatsapp
              : null,
        cpf_final: typeof c.cpf_final === 'string' ? c.cpf_final : null,
        tem_cpf: c.tem_cpf === true,
        tem_nascimento: c.tem_nascimento === true,
        ultimo_acesso: typeof c.ultimo_acesso === 'string' ? c.ultimo_acesso : null,
      };
    })
    .filter((c) => c.usuario_id.length > 0);
}

// O link gerado: mensagem pronta para o WhatsApp e o horário em que vence.
export function normalizarLinkSenha(data: unknown): LinkSenha | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.ok !== true) return null;
  const link = typeof d.link === 'string' ? d.link : '';
  if (!link) return null;
  return {
    link,
    expira_em: typeof d.expira_em === 'string' ? d.expira_em : null,
    pede_cpf: d.pede_cpf === true,
    pede_nascimento: d.pede_nascimento === true,
    aviso: typeof d.aviso === 'string' && d.aviso ? d.aviso : null,
    mensagem:
      typeof d.mensagem === 'string' && d.mensagem
        ? d.mensagem
        : `Se Liga: aqui está o seu link para criar uma nova senha. Ele vale 10 minutos: ${link}`,
  };
}

// Minutos que ainda faltam para o link vencer (0 quando já venceu).
export function minutosRestantes(expiraEm: string | null, agora: number): number {
  if (!expiraEm) return 0;
  const fim = new Date(expiraEm).getTime();
  if (Number.isNaN(fim)) return 0;
  return Math.max(0, Math.ceil((fim - agora) / 60000));
}

// ---- Prévia dos avisos gerais (aviso_geral_previa) ----

function normalizarApp(data: unknown): AppInstalado {
  const d = (data ?? {}) as Record<string, unknown>;
  return {
    aparelhos_vistos: numero(d.aparelhos_vistos),
    instalados: numero(d.instalados),
    instalados_android: numero(d.instalados_android),
    instalados_ios: numero(d.instalados_ios),
    abriram_7d: numero(d.abriram_7d),
    contando_desde: typeof d.contando_desde === 'string' ? d.contando_desde : null,
  };
}

export function normalizarPrevia(data: unknown): PreviaAviso | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  return {
    filtrado: Boolean(d.filtrado),
    pessoas: numero(d.pessoas),
    aparelhos: numero(d.aparelhos),
    leads_no_grupo: numero(d.leads_no_grupo),
    contas: numero(d.contas),
    ativas_30d: numero(d.ativas_30d),
    enviados_24h: numero(d.enviados_24h),
    limite_dia: numero(d.limite_dia) || 3,
    app: normalizarApp(d.app),
  };
}

// ---- Opções de filtro (leads_opcoes) ----

function stringDe(v: unknown): string {
  return typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v);
}

export function normalizarOpcoes(data: unknown): OpcoesLeads {
  const d = (data ?? {}) as Record<string, unknown>;
  const eventosBrutos = Array.isArray(d.eventos) ? d.eventos : [];
  const cidadesBrutos = Array.isArray(d.cidades) ? d.cidades : [];
  const origensBrutos = Array.isArray(d.origens) ? d.origens : [];
  return {
    eventos: eventosBrutos.map((item) => {
      const e = (item ?? {}) as Record<string, unknown>;
      return { valor: stringDe(e.id), rotulo: stringDe(e.nome), quantidade: numero(e.leads) };
    }),
    cidades: cidadesBrutos.map((item) => {
      const c = (item ?? {}) as Record<string, unknown>;
      return { valor: stringDe(c.cidade), rotulo: stringDe(c.cidade), quantidade: numero(c.leads) };
    }),
    origens: origensBrutos.map((item) => {
      const o = (item ?? {}) as Record<string, unknown>;
      return { valor: stringDe(o.origem), rotulo: stringDe(o.rotulo), quantidade: numero(o.leads) };
    }),
  };
}

export function opcoesVazias(): OpcoesLeads {
  return { eventos: [], cidades: [], origens: [] };
}

// ---- Resumo e lista de leads ----

export function normalizarResumo(data: unknown): ResumoLeads | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  return {
    total: numero(d.total),
    com_telefone: numero(d.com_telefone),
    com_email: numero(d.com_email),
    com_conta: numero(d.com_conta),
    recebem_avisos: numero(d.recebem_avisos),
    aceitam_whatsapp: numero(d.aceitam_whatsapp),
    descadastrados: numero(d.descadastrados),
    ultima_sincronizacao: typeof d.ultima_sincronizacao === 'string' ? d.ultima_sincronizacao : null,
  };
}

function listaStrings(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => stringDe(x)).filter((x) => x.length > 0);
}

export function normalizarLeads(data: unknown): Lead[] {
  if (!Array.isArray(data)) return [];
  return data.map((item) => {
    const l = (item ?? {}) as Record<string, unknown>;
    return {
      id: numero(l.id),
      nome: typeof l.nome === 'string' ? l.nome : null,
      telefone: typeof l.telefone === 'string' ? l.telefone : null,
      email: typeof l.email === 'string' ? l.email : null,
      cidade: typeof l.cidade === 'string' ? l.cidade : null,
      uf: typeof l.uf === 'string' ? l.uf : null,
      origens: listaStrings(l.origens),
      eventos: listaStrings(l.eventos),
      tem_conta: Boolean(l.tem_conta),
      recebe_avisos: Boolean(l.recebe_avisos),
      aceita_whatsapp:
        l.aceita_whatsapp === true ? true : l.aceita_whatsapp === false ? false : null,
      primeiro_em: typeof l.primeiro_em === 'string' ? l.primeiro_em : null,
      ultimo_em: typeof l.ultimo_em === 'string' ? l.ultimo_em : null,
    };
  });
}

// ---- Orações do admin (oracoes_admin_listar) ----

export function normalizarOracoesAdmin(data: unknown): OracaoAdmin[] {
  if (!Array.isArray(data)) return [];
  return data.map((item) => {
    const o = (item ?? {}) as Record<string, unknown>;
    return {
      dia: stringDe(o.dia),
      evangelho_ref: typeof o.evangelho_ref === 'string' ? o.evangelho_ref : null,
      tema: typeof o.tema === 'string' ? o.tema : null,
      tempo_liturgico: typeof o.tempo_liturgico === 'string' ? o.tempo_liturgico : null,
      cor_liturgica: typeof o.cor_liturgica === 'string' ? o.cor_liturgica : null,
      santo: typeof o.santo === 'string' ? o.santo : null,
      santo_titulo: typeof o.santo_titulo === 'string' ? o.santo_titulo : null,
      santo_texto: typeof o.santo_texto === 'string' ? o.santo_texto : null,
      santo_pedido: typeof o.santo_pedido === 'string' ? o.santo_pedido : null,
      leitura_guia: typeof o.leitura_guia === 'string' ? o.leitura_guia : null,
      resumo: typeof o.resumo === 'string' ? o.resumo : null,
      reflexao: typeof o.reflexao === 'string' ? o.reflexao : null,
      perguntas: typeof o.perguntas === 'string' ? o.perguntas : null,
      texto: typeof o.texto === 'string' ? o.texto : null,
      silencio: typeof o.silencio === 'string' ? o.silencio : null,
      gesto: typeof o.gesto === 'string' ? o.gesto : null,
      catecismo: typeof o.catecismo === 'string' ? o.catecismo : null,
      aviso: typeof o.aviso === 'string' ? o.aviso : null,
      imagem_url: typeof o.imagem_url === 'string' && o.imagem_url ? o.imagem_url : null,
      imagem_credito:
        typeof o.imagem_credito === 'string' && o.imagem_credito ? o.imagem_credito : null,
      video_url: typeof o.video_url === 'string' && o.video_url ? o.video_url : null,
      video_autor: typeof o.video_autor === 'string' && o.video_autor ? o.video_autor : null,
      aprovada: o.aprovada === true,
      aprovada_em: typeof o.aprovada_em === 'string' ? o.aprovada_em : null,
      rezaram: numero(o.rezaram),
      aceitaram: numero(o.aceitaram),
    };
  });
}

// ---- Telefone (vem como 55DDDNÚMERO) ----

export function somenteDigitos(v?: string | null): string {
  return (v ?? '').replace(/\D/g, '');
}

// Formata para (14) 99999-9999.
export function formatarTelefone(v?: string | null): string {
  let d = somenteDigitos(v);
  if (!d) return '';
  if (d.startsWith('55') && d.length > 11) d = d.slice(2);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return v ?? '';
}

// Número pronto para o link do WhatsApp (com o 55 do Brasil na frente).
export function telefoneParaWa(v?: string | null): string {
  const d = somenteDigitos(v);
  if (!d) return '';
  return d.startsWith('55') ? d : `55${d}`;
}

// ---- Descrição do grupo escolhido ----

export function filtrosVazios(f: FiltrosLeads): boolean {
  return (
    (!f.eventos || f.eventos.length === 0) &&
    (!f.cidades || f.cidades.length === 0) &&
    (!f.origens || f.origens.length === 0)
  );
}

// Texto curto do grupo (ex.: "Acampamento · Compradores da ação entre amigos").
export function descreverGrupo(f: FiltrosLeads, opcoes: OpcoesLeads): string {
  const partes: string[] = [];
  const nomesEventos = opcoes.eventos
    .filter((o) => f.eventos.includes(Number(o.valor)))
    .map((o) => o.rotulo);
  const nomesCidades = opcoes.cidades
    .filter((o) => f.cidades.includes(o.valor))
    .map((o) => o.rotulo);
  const nomesOrigens = opcoes.origens
    .filter((o) => f.origens.includes(o.valor))
    .map((o) => o.rotulo);
  partes.push(...nomesEventos, ...nomesCidades, ...nomesOrigens);

  if (partes.length === 0) return 'Todos';
  const mostrados = partes.slice(0, 3).join(' · ');
  const restante = partes.length - 3;
  return restante > 0 ? `${mostrados} · +${restante}` : mostrados;
}

// ---- CSV (Excel: separador ; e BOM) ----

function celulaCsv(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v);
  if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function montarCsvLeads(linhas: LeadExport[]): string {
  const cabecalho = [
    'Nome',
    'Telefone',
    'E-mail',
    'Cidade',
    'UF',
    'Origens',
    'Eventos',
    'Conta no app',
    'Recebe avisos',
    'Aceita WhatsApp',
    'Primeiro contato',
    'Último contato',
  ];
  const corpo = linhas.map((l) =>
    [
      l.nome ?? '',
      formatarTelefone(l.telefone),
      l.email ?? '',
      l.cidade ?? '',
      l.uf ?? '',
      l.origens ?? '',
      l.eventos ?? '',
      l.tem_conta ? 'Sim' : 'Não',
      l.recebe_avisos ? 'Sim' : 'Não',
      l.aceita_whatsapp === true ? 'Sim' : l.aceita_whatsapp === false ? 'Não' : '',
      formatarDataBR(l.primeiro_em),
      formatarDataBR(l.ultimo_em),
    ]
      .map(celulaCsv)
      .join(';'),
  );
  const todas = [cabecalho.join(';'), ...corpo];
  return `\uFEFF${todas.join('\r\n')}`;
}

export function nomeArquivoLeads(d: Date = new Date()): string {
  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `leads-seliga-${ano}-${mes}-${dia}.csv`;
}